'use client'
import React, { useEffect, useState, useRef } from 'react';
import Image from "next/image";
import { usePathname } from 'next/navigation';

import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { motion } from 'framer-motion'
import { gsap } from "gsap";
import { Draggable } from 'gsap/Draggable';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
import { SplitText } from 'gsap/SplitText';

import styles from './pageTransition.module.scss';
import LogoFooter from './LogoFooter';

gsap.registerPlugin(Draggable, InertiaPlugin, DrawSVGPlugin, SplitText);

interface LottieAnimationProps {
    animationData: any; // 
}

export default function PageTransition({children}: {children: React.ReactNode}) {
    const allAnimations = [ 'pictures', 'picturesMono', 'sentences', 'movie', 'lines', 'sdMono', 'sdColor', 'Inc' ];
    const excludedPaths = ['/','/about/','/projects/page/1/','/news/page/1/','/career/'];

    const pathname = usePathname();
    const [currentAnimation, setCurrentAnimation] = useState( allAnimations[Math.floor(Math.random() * allAnimations.length)] );
    const [pictureZIndex, setPictureZIndex] = useState<number>(10);
    const [isSplit, setIsSplit] = useState<boolean>(false);
    // const [isIncInHome, setIsIncInHome] = useState<boolean>(false);

    const textWrap = useRef<HTMLDivElement>(null);
    const incRef = useRef<LottieRefCurrentProps>(null);
    const incWrapRef = useRef<HTMLDivElement>(null);
    const incVideoRef = useRef<HTMLVideoElement>(null);
    const typeMonoRef = useRef<LottieRefCurrentProps>(null);
    const typeMonoWrapRef = useRef<HTMLDivElement>(null);
    const typeColorWrapRef = useRef<HTMLDivElement>(null);
    const typeColorAPng = useRef<HTMLImageElement>(null);
    const containerColorPicture = useRef<HTMLDivElement>(null);
    const containerLines = useRef<HTMLDivElement>(null);
    const containerMonoPicture = useRef<HTMLDivElement>(null);
    const containerMovie = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    let lineTl: gsap.core.Timeline;
    let isIncInHome : boolean = false;


    function isPcDevice() {
        return !('ontouchstart' in window) && !navigator.maxTouchPoints;
    }
    

    // TEXT ANIMATION
    const textAnimation = (wrapText: HTMLDivElement, isHome: boolean) => {
        // SPLIT TEXT
        if (!isSplit) {
            setIsSplit(true);            
            const textBlocks = wrapText.querySelectorAll('p');

            if(wrapText) {
                Array.from(textBlocks).map((textBlock: HTMLParagraphElement) => {
                    new SplitText(
                        textBlock,
                        {
                            type: 'chars',
                            charsClass: 'char',
                        }
                    )
                })
            }
        }
        const textBlocks = wrapText.querySelectorAll('p');

        function statePause() {
            if(wrapText) {
                wrapText.classList.add('animation-play-state-paused');
                wrapText.classList.remove('animation-play-state-running');
                wrapText.classList.add('invisible');
            }
        }

        function stateRun() {
            if(wrapText) {
                wrapText.classList.remove('animation-play-state-paused');
                wrapText.classList.add('animation-play-state-running');
                wrapText.classList.remove('invisible');
            }
        }

        function fadeOutSentence (isHome: boolean = false) {
            const url = new URL(window.location.href);
            const currentPath = url.pathname;

            Array.from(textBlocks).map((paragraph: any, index: number) => {
                gsap.fromTo(
                    gsap.utils.toArray(paragraph.querySelectorAll('div')),
                    {
                        opacity: 1,
                        scale: 1,
                    },
                    {
                        opacity: 0,
                        scale: 0.8,
                        ease: "steps(2)",
                        duration: 1,
                        y: "random(-30, 30)px",
                        delay: index*0.3,
                        clearProps: 'opacity',
                        stagger: {
                            amount: 1.2,
                            from: 'random',
                        }
                    }
                );
            })
            
            if(currentPath === '/') {                
                setTimeout(() => {
                    statePause();
    
                    fadeInSentence(isHome);
                    console.log('fadeOutSentence on complete');
                }, 6000);
            }
        }

        function fadeInSentence (isHome: boolean = false) {
            const url = new URL(window.location.href);
            const currentPath = url.pathname;
            // console.log(currentPath);

            stateRun();

            Array.from(textBlocks).map((paragraph: any, index: number) => {
                gsap.fromTo(
                    gsap.utils.toArray(paragraph.querySelectorAll('div')),
                    {
                        opacity: 0,
                        scale: 0.8,
                        y: "random(-5, 5)px",
                    },
                    {
                        opacity: 1,
                        scale: 1,
                        y: Math.random() < 0.3 ? `random(-3, 3)px` : 0,
                        ease: "steps(2)",
                        duration: 1,
                        delay: index*0.3,
                        stagger: {
                            amount: 1.2,
                            from: 'random',
                        }
                    }
                );
            })

            if(currentPath === '/') {
                setTimeout(() => {
                    console.log('fadeInSentence on complete');
                    fadeOutSentence(isHome);
                }, 6000)            
            }
        }

        setTimeout(() => {
            if(wrapText) {
                if(!isHome) {
                    fadeInSentence();
                    setTimeout(() => {
                        fadeOutSentence();
                    }, 3000);
                } else {
                    fadeInSentence(true);
                }
            }
        }, 100);
    }

    // LOTTIE ANIMATION
    const animationLottie = (el: any, isHome: boolean, totalTime?: number, isTypeMono?: boolean) => {
        el?.play();
        if(isHome) {
            if(isTypeMono) {
                setTimeout(() => {                    
                    const topLogo = document.querySelector('.js-footer-logo');
                    gsap.set(topLogo, {opacity: 0});
                }, 100);
            }
        } else {
            totalTime && 
                setTimeout(() => {                    
                    el.goToAndStop(0, true);
                    // el.pause(true);
                }, totalTime);
        }
    }

    // LNC ANIMATION
    const animationInc = (elPc: any, elSp: any, isHome: boolean, isPc: boolean) => {
        if(isPc) {
            elPc.play();

            if(!isHome) {
                isIncInHome = false;
                setTimeout(() => {
                    if(!isIncInHome) {
                        elPc.goToAndStop(0, true);
                    }
                }, 12000);
            } else {
                isIncInHome = true;
            }
        } else {
            elSp.play();

            if(!isHome) {
                isIncInHome = false;

                setTimeout(() => {
                    if(!isIncInHome) {
                        elSp.pause();
                        elSp.currentTime = 0;
                    }
                }, 9500);
            } else {
                isIncInHome = true;

                elSp.addEventListener('ended', () => {
                    console.log('Inc SP on ended');

                    console.log(isIncInHome);
                    if (isIncInHome === true) {
                        setTimeout(() => {
                            elSp.currentTime = 0; // 再生位置を最初に戻す
                            elSp.play(); // 再生を開始
                        }, 4500); // 3000ミリ秒 = 3秒
                    }
                });
            }
        }
    }

    // MOVIE
    const movieAnimation = (wrapVideo: HTMLDivElement, video: HTMLVideoElement, isHome: boolean) => {
        const insideDiv = wrapVideo.querySelector('div');
        const tlEnter = gsap.timeline({paused: true});
        const tlLeave = gsap.timeline({paused: true});
        
        tlEnter.fromTo(
            wrapVideo,
            {
                x: 'random([-100, 100])vw',
                y: `random([-100, 100])vh`,
                rotate: 0,
                opacity: 1,
            },
            {
                duration: 1,
                x: 0,
                y: 0,
                rotate: 'random(-6, 6)deg',
                opacity: 1,
                ease: "power4.out",
                onStart: ()=> {
                    video?.play();
                    if(insideDiv) {
                        insideDiv.classList.add('pointer-events-auto');
                        insideDiv.classList.remove('pointer-events-none');
                        gsap.set(insideDiv, {x: 0, y: 0});
                    }
                },
                onComplete: ()=> {!isHome && tlLeave.play()}
            }
        )

        tlLeave.to(
            wrapVideo, {
                duration: 0.1,
                scaleY: 0,
                scaleX: 1.5,
                // ease: "power4.in",
                delay: 1.5,
                clearProps: "scaleY,scaleX",
                onComplete: ()=> {
                    video?.pause();
                    if(insideDiv) {                
                        gsap.set(wrapVideo, {opacity: 0});
                        insideDiv.classList.remove('pointer-events-auto');
                        insideDiv.classList.add('pointer-events-none');
                        video.style.filter = 'grayscale(0) contrast(1)';
                    }
                }
            }
        )

        tlEnter.play();
    }

    // PICTURES
    const pictureAnimation = (wrapPicture: HTMLDivElement, isHome: boolean) => {
        const bodyHeight = document.body.offsetHeight;
        const picsShuffle = gsap.utils.shuffle(gsap.utils.toArray(wrapPicture.querySelectorAll('.js-pic-w')));
        wrapPicture.style.height = `${bodyHeight}px`;
        setPictureZIndex(10);

        // SET START POSITION OF DRACABLE ITEMS
        picsShuffle.map((el: any, index: number) => {
            index++
            gsap.set(el, {zIndex: index})
            gsap.set(el.querySelector('div'), {x: 0, y: 0})
        })

        gsap.fromTo(
            picsShuffle, 
            {
                scale: 1.1,
                rotate: `random(-16, 16)deg`,
                opacity: 0,
            },
            {
                duration: 0.3,
                scale: 1,
                rotate: `random(-6, 6)deg`,
                opacity: 1,
                ease: "power4.inOut",
                stagger: {
                    each: 0.1,
                },
                onStart: ()=> {
                    gsap.set(wrapPicture, {visibility: 'visible'})
                    picsShuffle.map((el: any) => {
                        gsap.set(el.querySelector('div'), {pointerEvents: 'auto'})
                        gsap.set(gsap.utils.toArray(wrapPicture.querySelectorAll('.js-pic-w div')), {x: 0, y: 0})
                    })
                },
                onComplete: ()=> {
                    if(!isHome) {
                        gsap.to(picsShuffle, {
                            duration: 1,
                            x: `random([-150, 150])vw`,
                            y: `random([-150, 150])vh`,
                            opacity: 1,
                            delay: 1,
                            clearProps: "x,y",
                            // ease: "steps(12)",
                            ease: "power4.inOut",

                            stagger: {
                                each: 0.05,
                            },
                            onComplete: ()=> {
                                gsap.set(wrapPicture, {visibility: 'hidden'})
                                gsap.set(picsShuffle, {opacity: 0, zIndex: 0})
                            }
                        })
                    }
                }
            }
        )
    }

    const typoColorAnimation = (tcFile: HTMLDivElement, isHome: boolean) => {
        const imgAPng = tcFile.querySelector('img');

        if(imgAPng) {
            imgAPng.src = typeColorAPng.current?.getAttribute('src-path') ?? '';
            gsap.set(imgAPng, {opacity: 1});

            if(!isHome) {
                imgAPng?.addEventListener('load', () => {
                    setTimeout(() => {
                        imgAPng && (imgAPng.src = '');
                        gsap.to(imgAPng, {duration: 0.4, opacity: 0});
                    },3200)
                })
            }
        }
    }

    // LINES
    const linesAnimation = (wrapLines: HTMLDivElement, isHome: boolean) => {
        const allLines = gsap.utils.toArray(wrapLines.querySelectorAll('.wrap-line svg >path'));
        const randomThreeLines = gsap.utils.shuffle(allLines).slice(0, 3);
        const randomLines = gsap.utils.shuffle(allLines);
        const lines = isHome ? randomLines : randomThreeLines;
        lineTl = gsap.timeline({paused: true});

        const lineDuration = isHome ? 2 : 1.8;
        const delayStagger = isHome ? 3 : 2.6;
        const delayExit = isHome ? '<=2' : '<=1.8';

        // SET START POSITION OF DRACABLE ITEMS
        lines.map((el: any, index: number) => {
            index++
            gsap.set(el, {zIndex: index})
        })

        lineTl.fromTo(
            lines,
            {
                drawSVG: '0%',
            },
            {
                drawSVG: '100%',
                duration: lineDuration,
                ease: "power3.inOut",

                stagger: {
                    each: delayStagger,
                },
                onStart: ()=> {
                    gsap.set(wrapLines, {visibility: 'visible'})
                    gsap.set(allLines, {drawSVG: '0%'})
                },
            }
        )
            .to(
                lines,
                {
                    drawSVG: '100% 100%',
                    duration: lineDuration,
                    ease: "power3.inOut",

                    stagger: {
                        each: delayStagger,
                    },
                },
                delayExit
            )

        if(isHome) {
            lineTl.repeat(-1).play()
        } else {
            lineTl.play();
        }
    }
    
    const incContent = incWrapRef.current &&
        isPcDevice() ?
            (
                <Lottie
                    lottieRef={incRef}
                    className='lottie-inc scale-[1.5] w-full h-full object-cover lg:origin-top lg:scale-[1.1]'
                    animationData={
                        require(`@/public/animation/inc.json`)
                    }
                    loop={true}
                    autoplay={false}
                    onComplete={() => incRef.current?.goToAndStop(0, true)}
                    rendererSettings={{
                        progressiveLoad: true,
                        hideOnTransparent: true,
                    }}
                />
            ) : (
                incWrapRef.current && incWrapRef.current.clientWidth >= 768 ? (
                    <video ref={incVideoRef} src="/animation/inc-tablet.mp4" muted playsInline className='w-full h-full object-cover'></video>
                ) : (
                    <video ref={incVideoRef} src="/animation/inc-sp.mp4" muted playsInline className='w-full h-full object-cover'></video>
                )
            );

    const changeZ = (e: any) => {

        setPictureZIndex(pictureZIndex + 1);
        const targetElement = e.target as HTMLDivElement | null;
        console.log(targetElement);
        if (targetElement) {
            const closestElement = targetElement.closest('.js-pic-w');
            if (closestElement instanceof HTMLElement) {
                closestElement.style.zIndex = String(pictureZIndex);
            }
        }        
    }

    useEffect(() => {
        const isExcludedPath = excludedPaths.includes(pathname);
        const isWindowSizeMoreThan1024 = window.innerWidth >= 1024;
        const isHome = pathname == '/';

        // console.log('pathname: ' + pathname);
        // console.log('isHome: ' + isHome);

        if(isWindowSizeMoreThan1024 && isHome) {
            const body = document.querySelector('body');
            !body?.classList.contains('overflow-hidden') && body?.classList.add('overflow-hidden');
        } else {
            const body = document.querySelector('body');
            body?.classList.contains('overflow-hidden') && body?.classList.remove('overflow-hidden');
        }

        // setCurrentChildren(children);
        function getRandomAnimation() {
            const filteredAnimations = allAnimations.filter((animation) => animation !== currentAnimation);
            const randomIndex = Math.floor(Math.random() * filteredAnimations.length);
            return filteredAnimations[randomIndex];
        }
        
        // アニメーション発火判定
        if (isExcludedPath) {
            setCurrentAnimation(getRandomAnimation());
            // console.log('currentAnimation: ' + currentAnimation);

            // containerMovie.current && videoRef.current && movieAnimation(containerMovie.current, videoRef.current, isHome);
            // containerMonoPicture.current && pictureAnimation(containerMonoPicture.current, isHome);
            // typeMonoRef.current && animationLottie(typeMonoRef.current, isHome, 16000, true);
            // setTimeout(() => animationInc(incRef.current, incVideoRef.current, isHome, isPcDevice()), 500)
            // containerColorPicture.current && pictureAnimation(containerColorPicture.current, isHome);
            // typeColorWrapRef.current && typoColorAnimation(typeColorWrapRef.current, isHome);
            // textWrap.current && textAnimation(textWrap.current, isHome);
            // containerLines.current && linesAnimation(containerLines.current, isHome);

            currentAnimation === 'pictures' && containerColorPicture.current && pictureAnimation(containerColorPicture.current, isHome);
            currentAnimation === 'picturesMono' && containerMonoPicture.current && pictureAnimation(containerMonoPicture.current, isHome);
            currentAnimation === 'movie' && containerMovie.current && videoRef.current && movieAnimation(containerMovie.current, videoRef.current, isHome);
            currentAnimation === 'Inc' && setTimeout(() => animationInc(incRef.current, incVideoRef.current, isHome, isPcDevice()), 500)
            currentAnimation === 'sdMono' && animationLottie(typeMonoRef.current, isHome, 14500, true);
            currentAnimation === 'sdColor' && typeColorWrapRef.current && typoColorAnimation(typeColorWrapRef.current, isHome);
            currentAnimation === 'lines' && containerLines.current && linesAnimation(containerLines.current, isHome);
            currentAnimation === 'sentences' && textWrap.current && textAnimation(textWrap.current, isHome);
        }

        return () => {
            if(isHome) {
                if(currentAnimation === 'movie' && containerMovie.current) {
                    const wrapVideo = containerMovie.current;
                    const insideDiv = containerMovie.current.querySelector('div');
                    if(wrapVideo && insideDiv) {
                        gsap.to(wrapVideo, 
                            {
                                duration: 0.1,
                                scaleY: 0,
                                scaleX: 1.5,
                                // ease: "power4.in",
                                delay: 1,
                                clearProps: "scaleY,scaleX",
                                onComplete: ()=> {
                                    videoRef.current?.pause();
                                    if(insideDiv) {                
                                        gsap.set(wrapVideo, {opacity: 0});
                                        insideDiv.classList.remove('pointer-events-auto');
                                        insideDiv.classList.add('pointer-events-none');
                                    }
                                }
                            }
                        )
                    }
                }

                currentAnimation === 'pictures' && containerColorPicture.current && gsap.to(gsap.utils.shuffle(gsap.utils.toArray(containerColorPicture.current.querySelectorAll('.js-pic-w'))), {
                    duration: 1,
                    x: `random([-150, 150])vw`,
                    y: `random([-150, 150])vh`,
                    // ease: "steps(12)",
                    ease: "power4.inOut",

                    opacity: 1,
                    delay: 1,
                    clearProps: "x,y",
                    stagger: {
                        each: 0.05,
                    },
                    onComplete: ()=> {
                        if(containerColorPicture.current) {
                            gsap.set(containerColorPicture.current, {visibility: 'hidden'})
                            gsap.set(gsap.utils.toArray(containerColorPicture.current.querySelectorAll('.js-pic-w')), {zIndex: 0})
                        }
                    }
                })

                currentAnimation === 'picturesMono' && containerMonoPicture.current && gsap.to(gsap.utils.shuffle(gsap.utils.toArray(containerMonoPicture.current.querySelectorAll('.js-pic-w'))), {
                    duration: 1,
                    x: `random([-150, 150])vw`,
                    y: `random([-150, 150])vh`,
                    // ease: "steps(12)",
                    ease: "power4.inOut",
                    
                    opacity: 1,
                    delay: 1,
                    clearProps: "x,y",
                    stagger: {
                        each: 0.05,
                    },
                    onComplete: ()=> {
                        if(containerMonoPicture.current) {
                            gsap.set(containerMonoPicture.current, {visibility: 'hidden'})
                            gsap.set(gsap.utils.toArray(containerMonoPicture.current.querySelectorAll('.js-pic-w')), {zIndex: 0})
                        }
                    }
                })

                currentAnimation == 'sdColor' && typeColorWrapRef.current &&
                    gsap.to(typeColorWrapRef.current, {
                        duration: 0.2,
                        opacity: 0,
                        clearProps: "opacity",
                        onComplete: ()=> {
                            const imgElement = typeColorWrapRef.current?.querySelector('img');
                            if (imgElement) {
                                imgElement.src = '';
                            }
                        }
                    })

                currentAnimation == 'sdMono' &&
                    gsap.set(document.querySelector('.js-footer-logo'), {opacity: 1});

                currentAnimation == 'sentences' && textWrap.current &&
                    setTimeout(() => {
                        const textBlocks = textWrap.current?.querySelectorAll('p');

                        if(textBlocks) {
                            Array.from(textBlocks).map((paragraph: any, index: number) => {
                                gsap.fromTo(
                                    gsap.utils.toArray(paragraph.querySelectorAll('div')),
                                    {
                                        opacity: 1,
                                        scale: 1,
                                    },
                                    {
                                        opacity: 0,
                                        scale: 0.8,
                                        y: Math.random() < 0.3 ? `random(-5, 5)px` : 0,
                                        ease: "steps(2)",
                                        duration: 1,
                                        delay: index*0.3,
                                        clearProps: 'opacity',
                                        stagger: {
                                            amount: 1.2,
                                            from: 'random',
                                        },
                                        onComplete: () => {
                                            setTimeout(() => {
                                                if(textWrap.current) {
                                                    textWrap.current.classList.add('animation-play-state-paused');
                                                    textWrap.current.classList.remove('animation-play-state-running');
                                                    textWrap.current.classList.add('invisible');
                                                }
                                            }, 2000);
                                        }
                                    }
                                );
                            })
                        }
                    }, 500);
            }

            if(currentAnimation === 'pictures' || currentAnimation === 'picturesMono') {
                containerColorPicture.current && (containerColorPicture.current.style.height = 'auto');
                containerMonoPicture.current && (containerMonoPicture.current.style.height = 'auto');
            }

            currentAnimation === 'sdMono' && typeMonoWrapRef.current && 
                gsap.to(typeMonoWrapRef.current, {
                    duration: 0.2,
                    opacity: 0,
                    clearProps: "opacity",
                    onComplete: ()=> {
                        typeMonoRef.current && typeMonoRef.current.goToAndStop(0, true);
                    }
                })

            currentAnimation === 'Inc' && incWrapRef.current && 
                gsap.to(incWrapRef.current, {
                    duration: 0.2,
                    opacity: 0,
                    clearProps: "opacity",
                    onComplete: () => {
                        if (isWindowSizeMoreThan1024) {
                            incRef.current?.goToAndStop(0, true);
                        } else {                            
                            if (incVideoRef.current) {
                                incVideoRef.current.pause();
                                incVideoRef.current.currentTime = 0;
                            }
                        }
                    }
                });

            currentAnimation === 'lines' && containerLines.current &&
                gsap.to(containerLines.current, {
                    duration: 0.2,
                    opacity: 0,
                    clearProps: "opacity",
                    onComplete: ()=> {
                        lineTl && lineTl.restart().pause();
                    }
                })             
        }
    }, [pathname, children]);


    return (
        <>
            {/* TEXT BLOCKS */}
            <div className="absolute top-0 left-0 w-full h-svh pointer-events-none overflow-hidden z-[7]">
                <div ref={textWrap} className={`${styles.sentence} sentence-pt warp-sentence font-TsukuOldMin w-fit flex justify-start items-start gap-x-[50px] px-[25px] mt-[20vh] text-[1.4rem] text-nowrap lg:text-f-vw-18 lg:gap-x-[6.94vw] lg:px-[3.47vw] animation-play-state-paused invisible`}>
                    <p>ブランディングデザインとは、<br/>健在化していない本質をすくい取り、可視化する行為です。<br/><br/>クライアントの過去・現在・未来に寄り添いながら、<br/>社会の複雑さや曖昧さによって見えにくくなってしまった本質を抽出し、<br/>言語と意匠の力でその価値を磨き上げていくことが私たちの役割です。<br/><br/>あらゆるコミュニケーションを点ではなく線としてつないでいくことで、<br/>本質的な価値をひとつのストーリーとして社会の中に紡いでいきます。<br/><br/>デザインできるものの全てを、<br/>点ではなく線にして、<br/>社会の右脳を刺激していきます。</p>
                    <p>ブランディングデザインとは、<br/>健在化していない本質をすくい取り、可視化する行為です。<br/><br/>クライアントの過去・現在・未来に寄り添いながら、<br/>社会の複雑さや曖昧さによって見えにくくなってしまった本質を抽出し、<br/>言語と意匠の力でその価値を磨き上げていくことが私たちの役割です。<br/><br/>あらゆるコミュニケーションを点ではなく線としてつないでいくことで、<br/>本質的な価値をひとつのストーリーとして社会の中に紡いでいきます。<br/><br/>デザインできるものの全てを、<br/>点ではなく線にして、<br/>社会の右脳を刺激していきます。</p>
                    <p>ブランディングデザインとは、<br/>健在化していない本質をすくい取り、可視化する行為です。<br/><br/>クライアントの過去・現在・未来に寄り添いながら、<br/>社会の複雑さや曖昧さによって見えにくくなってしまった本質を抽出し、<br/>言語と意匠の力でその価値を磨き上げていくことが私たちの役割です。<br/><br/>あらゆるコミュニケーションを点ではなく線としてつないでいくことで、<br/>本質的な価値をひとつのストーリーとして社会の中に紡いでいきます。<br/><br/>デザインできるものの全てを、<br/>点ではなく線にして、<br/>社会の右脳を刺激していきます。</p>
                    <p>ブランディングデザインとは、<br/>健在化していない本質をすくい取り、可視化する行為です。<br/><br/>クライアントの過去・現在・未来に寄り添いながら、<br/>社会の複雑さや曖昧さによって見えにくくなってしまった本質を抽出し、<br/>言語と意匠の力でその価値を磨き上げていくことが私たちの役割です。<br/><br/>あらゆるコミュニケーションを点ではなく線としてつないでいくことで、<br/>本質的な価値をひとつのストーリーとして社会の中に紡いでいきます。<br/><br/>デザインできるものの全てを、<br/>点ではなく線にして、<br/>社会の右脳を刺激していきます。</p>
                    <p>ブランディングデザインとは、<br/>健在化していない本質をすくい取り、可視化する行為です。<br/><br/>クライアントの過去・現在・未来に寄り添いながら、<br/>社会の複雑さや曖昧さによって見えにくくなってしまった本質を抽出し、<br/>言語と意匠の力でその価値を磨き上げていくことが私たちの役割です。<br/><br/>あらゆるコミュニケーションを点ではなく線としてつないでいくことで、<br/>本質的な価値をひとつのストーリーとして社会の中に紡いでいきます。<br/><br/>デザインできるものの全てを、<br/>点ではなく線にして、<br/>社会の右脳を刺激していきます。</p>
                    <p>ブランディングデザインとは、<br/>健在化していない本質をすくい取り、可視化する行為です。<br/><br/>クライアントの過去・現在・未来に寄り添いながら、<br/>社会の複雑さや曖昧さによって見えにくくなってしまった本質を抽出し、<br/>言語と意匠の力でその価値を磨き上げていくことが私たちの役割です。<br/><br/>あらゆるコミュニケーションを点ではなく線としてつないでいくことで、<br/>本質的な価値をひとつのストーリーとして社会の中に紡いでいきます。<br/><br/>デザインできるものの全てを、<br/>点ではなく線にして、<br/>社会の右脳を刺激していきます。</p>
                    <p>ブランディングデザインとは、<br/>健在化していない本質をすくい取り、可視化する行為です。<br/><br/>クライアントの過去・現在・未来に寄り添いながら、<br/>社会の複雑さや曖昧さによって見えにくくなってしまった本質を抽出し、<br/>言語と意匠の力でその価値を磨き上げていくことが私たちの役割です。<br/><br/>あらゆるコミュニケーションを点ではなく線としてつないでいくことで、<br/>本質的な価値をひとつのストーリーとして社会の中に紡いでいきます。<br/><br/>デザインできるものの全てを、<br/>点ではなく線にして、<br/>社会の右脳を刺激していきます。</p>
                    <p>ブランディングデザインとは、<br/>健在化していない本質をすくい取り、可視化する行為です。<br/><br/>クライアントの過去・現在・未来に寄り添いながら、<br/>社会の複雑さや曖昧さによって見えにくくなってしまった本質を抽出し、<br/>言語と意匠の力でその価値を磨き上げていくことが私たちの役割です。<br/><br/>あらゆるコミュニケーションを点ではなく線としてつないでいくことで、<br className="hidden lg:block"/>本質的な価値をひとつのストーリーとして社会の中に紡いでいきます。<br/><br/>デザインできるものの全てを、<br/>点ではなく線にして、<br/>社会の右脳を刺激していきます。</p>
                </div>
            </div>

            {/* LOTTIE INC ANIMATION */}
            <div ref={incWrapRef} className={`fixed top-0 left-0 w-screen h-[100vh] overflow-hidden pointer-events-none z-[6]`} >
                {incContent}
            </div>

            {/* LINES */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden invisible z-[8]">
                <div ref={containerLines} className={`${styles.bgLine} relative w-fill h-full`}>
                    <div className="wrap-line absolute top-0 left-1/2 w-[100vh] translate-x-[-50%] translate-y-[30vh] rotate-[90deg] lg:w-[80vw] lg:rotate-[0deg] lg:translate-x-[-40%] lg:translate-y-[10vw]">
                        <svg width="1019" height="542" viewBox="0 0 1019 542" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                                <pattern id="imagePattern" patternUnits="userSpaceOnUse" width="100" height="100" className='w-full h-full'>
                                    <image href="/images/transition/textureLines.webp" width="100" height="100" className='w-auto h-auto'/>
                                </pattern>
                                <filter id="pencil-texture">
                                    <feTurbulence type="fractalNoise" baseFrequency="0.05" numOctaves="5" result="noise"/>
                                    <feDisplacementMap in="SourceGraphic" in2="noise" scale="5" xChannelSelector="R" yChannelSelector="G"/>
                                </filter>
                            </defs>                        
                            <path d="M837.5 2.5C694.5 4 454.5 72.5 274.5 184.5C94.5 296.5 -33.5 474.5 13 526C59.5 577.5 294.5 464.5 408 409.5C521.5 354.5 883 160.5 837.5 111C792 61.5 645 147 613.5 169C582 191 431 308 482 380C533 452 894 271 899.5 281C905 291 751.5 350 761.5 372.5C771.5 395 995 309.5 997.5 316.5C1000 323.5 975.5 336 980.5 339.5C984.5 342.3 1006.17 332 1016.5 326.5" strokeWidth="5" strokeLinecap="round" fill="none" filter={`${isPcDevice() ? "url(#pencil-texture)" : "black"}`} stroke="url(#imagePattern)" strokeOpacity="1.0"/>
                        </svg>
                    </div>
                    <div className="wrap-line absolute top-0 left-1/2 w-[90vh] translate-x-[-60%] translate-y-[30vh] rotate-[0deg] lg:w-[70vw] lg:translate-x-[-40%]">
                        <svg width="1013" height="554" viewBox="0 0 1013 554" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1010 323C721.5 323 443.5 367.5 460 387.5C481.828 413.958 964 265.5 935 188.5C906 111.5 527.5 204 416 234C304.5 264 -1 388 2.99997 483C6.15784 558 235 587.539 443.5 483C624 392.5 822.5 158.951 670.5 3" strokeWidth="5" strokeLinecap="round" fill="none" filter={`${isPcDevice() ? "url(#pencil-texture)" : "black"}`} stroke="url(#imagePattern)" strokeOpacity="1.0"/>
                        </svg>
                    </div>
                    <div className="wrap-line absolute top-0 left-1/2 w-[100vh] translate-x-[-40%] translate-y-[35vh] rotate-[90deg] lg:w-[100vw] lg:rotate-[0deg] lg:translate-x-[-45%] lg:translate-y-[5vh]">
                        <svg width="1058" height="398" viewBox="0 0 1058 398" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M246 3C210.5 31.6667 127.9 107.7 81.5001 182.5C23.5001 276 -17.4999 368 13.5001 390.5C44.5001 413 136 349 195 277.5C254 206 284 169 273 161C262 153 189.661 252.069 221 275C262 305 437.5 137 428.5 125.5C416.734 110.465 312.485 270.489 360.5 305.5C408.5 340.5 601.5 106.167 587.5 87.5C572.5 67.5 504.5 185 530 210C555.5 235 637.5 161 632 152.5C624.882 141.5 581 197.5 600.5 214.5C620 231.5 660 221.5 725 159C716.333 173.333 701.9 204.1 713.5 212.5C728 223 780.5 221 832.5 141.5C820.167 158 799.7 193.9 816.5 205.5C842.5 223.452 941.814 126.364 921.5 118.5C906 112.5 871 196 899 209.5C930 224.446 979 187 1025.5 141.5C1021.17 152.5 1015.1 175.1 1025.5 177.5C1035.9 179.9 1049.5 167.833 1055 161.5" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" filter={`${isPcDevice() ? "url(#pencil-texture)" : "black"}`} stroke="url(#imagePattern)" strokeOpacity="1.0"/>
                        </svg>
                    </div>
                    <div className="wrap-line absolute top-1/2 left-1/2 w-[120vh] translate-x-[-50%] translate-y-[-20vh] rotate-[90deg] lg:top-0 lg:w-[90vw] lg:rotate-[0deg] lg:translate-x-[-40%] lg:translate-y-0">
                        <svg width="989" height="469" viewBox="0 0 989 469" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2.5 236C72 174.833 252.4 42.5 418 2.5C271 142 -3.42104 451.705 14 465C29.5 476.829 195.5 338.5 456.5 236C614.53 173.939 824.5 125.5 986.5 100" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" filter={`${isPcDevice() ? "url(#pencil-texture)" : "black"}`} stroke="url(#imagePattern)" strokeOpacity="1.0"/>
                        </svg>
                    </div>
                    <div className="wrap-line absolute top-0 left-1/2 w-[105vh] translate-x-[-50%] translate-y-[40vh] rotate-[90deg] lg:w-[90vw] lg:rotate-[0deg] lg:translate-x-[-40%] lg:translate-y-[10vh]">
                        <svg width="1071" height="552" viewBox="0 0 1071 552" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1068 160L625 482M605.5 497.5L637 549.5L1009 449.5V266L737.555 238.673M737.555 238.673L711 236L729 254.5L737.555 238.673ZM737.555 238.673L779 162L699 103L372 309.5L379.5 287L142 2.5L3 186L239.5 549.5L261 526.5" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" filter={`${isPcDevice() ? "url(#pencil-texture)" : "black"}`} stroke="url(#imagePattern)" strokeOpacity="1.0"/>
                        </svg>
                    </div>
                </div>
            </div>

            {children}

            {/* LOTTIE LOGO MONO */}
            <div ref={typeMonoWrapRef} className={`absolute top-0 left-0 w-screen h-svh overflow-hidden pointer-events-none z-[12]`} >
                <div className="absolute left-0 bottom-[4vh] origin-bottom-left scale-150 w-full h-auto lg:left-[-0.2vw] lg:bottom-[-4.5vw] lg:scale-[0.98]">
                    <Lottie
                        lottieRef={typeMonoRef}
                        animationData={require('@/public/animation/typeMono.json')}
                        loop={true}
                        autoplay={false}
                        className='w-full h-full'
                        onComplete={() => typeMonoRef.current?.goToAndStop(0, true)}
                    />
                </div>
            </div>

            {/* COLOR PICTURE */}
            <div ref={containerColorPicture} className="absolute top-0 left-0 w-screen h-svh pointer-events-none overflow-hidden lg:overflow-visible z-[14]">
                <div className="js-pic-w absolute scale-0 top-[15vh] left-1/2 w-[65vw] translate-x-[-50%] rotate-[-4deg] lg:w-[60vw] lg:rotate-[-6deg] lg:translate-x-[-60%] pointer-events-none">
                    <motion.div
                        whileDrag={{ scale: 1.015, rotate: 5, transition: { duration: 0.5 }}}
                        drag
                        onDragStart={(e: MouseEvent | TouchEvent | PointerEvent) => changeZ(e)}
                        className={`${styles.hoverItem} leading-[0] aspect-[3/5] hover:cursor-grab active:cursor-grabbing overflow-hidden pointer-events-none lg:aspect-[5/3]`}
                    >
                        <Image
                            className="w-full h-full object-cover pointer-events-none"
                            src='/images/transition/seitaro_color01.jpg'
                            alt="Image"
                            height={1336}
                            width={2000}
                            priority
                        />   
                    </motion.div>
                </div>
                <div className="js-pic-w absolute scale-0 top-[25vh] lg:top-[17vh] left-1/2 w-[65vw] translate-x-[-55%] rotate-[5deg] lg:w-[60vw] lg:rotate-6 pointer-events-none">
                    <motion.div
                        whileDrag={{ scale: 1.015, rotate: 5, transition: { duration: 0.5 }}}
                        drag
                        onDragStart={(e: MouseEvent | TouchEvent | PointerEvent) => changeZ(e)}
                        className={`${styles.hoverItem} leading-[0] aspect-[3/5] hover:cursor-grab active:cursor-grabbing overflow-hidden pointer-events-none lg:aspect-[5/3]`}
                    >
                        <Image
                            className="w-full h-full object-cover pointer-events-none"
                            src='/images/transition/seitaro_color02.jpg'
                            alt="Image"
                            height={1336}
                            width={2000}
                            priority
                        />   
                    </motion.div>                    
                </div>
                <div className="js-pic-w absolute scale-0 top-[8vh] lg:top-[13vh] left-[40%] lg:left-1/2 w-[65vw] translate-x-[-44%] rotate-[-8deg] lg:w-[60vw] lg:rotate-0 lg:translate-x-[-30%] lg:translate-y-[16%] pointer-events-none">
                    <motion.div
                        whileDrag={{ scale: 1.015, rotate: 5, transition: { duration: 0.5 }}}
                        drag
                        onDragStart={(e: MouseEvent | TouchEvent | PointerEvent) => changeZ(e)}
                        className={`${styles.hoverItem} leading-[0] aspect-[3/5] hover:cursor-grab active:cursor-grabbing overflow-hidden pointer-events-none lg:aspect-[5/3]`}
                    >
                        <Image
                            className="w-full h-full object-cover pointer-events-none"
                            src='/images/transition/seitaro_color03.jpg'
                            alt="Image"
                            height={1336}
                            width={2000}
                            priority
                        />   
                    </motion.div>                    
                </div>
                <div className="js-pic-w absolute scale-0 top-[12vh] left-1/2 w-[65vw] translate-x-[-44%] rotate-[6deg] lg:w-[60vw] lg:translate-x-[-42%] lg:rotate-[8deg] pointer-events-none">
                    <motion.div
                        whileDrag={{ scale: 1.015, rotate: 5, transition: { duration: 0.5 }}}
                        drag
                        onDragStart={(e: MouseEvent | TouchEvent | PointerEvent) => changeZ(e)}
                        className={`${styles.hoverItem} leading-[0] aspect-[3/5] hover:cursor-grab active:cursor-grabbing overflow-hidden pointer-events-none lg:aspect-[5/3]`}
                    >
                        <Image
                            className="w-full h-full object-cover pointer-events-none"
                            src='/images/transition/seitaro_color04.jpg'
                            alt="Image"
                            height={1336}
                            width={2000}
                            priority
                        />   
                    </motion.div>                    
                </div>
            </div>

            {/* MONO PICTURE */}
            <div ref={containerMonoPicture} className="absolute top-0 left-0 w-screen h-svh pointer-events-none overflow-hidden lg:overflow-visible z-[16]">
                <div className="js-pic-w absolute scale-0 top-[15vh] left-1/2 w-[65vw] translate-x-[-50%] rotate-[-4deg] lg:w-[60vw] lg:rotate-[-6deg] lg:translate-x-[-60%] pointer-events-none">
                    <motion.div
                        whileDrag={{ scale: 1.015, rotate: 5, transition: { duration: 0.5 }}}
                        drag
                        onDragStart={(e: MouseEvent | TouchEvent | PointerEvent) => changeZ(e)}
                        className={`${styles.hoverItem} leading-[0] aspect-[3/5] hover:cursor-grab active:cursor-grabbing overflow-hidden pointer-events-none lg:aspect-[5/3]`}
                    >
                        <Image
                            className="w-full h-full object-cover pointer-events-none"
                            src='/images/transition/seitaro_mono01.jpg'
                            alt="Image"
                            height={1336}
                            width={2000}
                            priority
                        />   
                    </motion.div>
                </div>
                <div className="js-pic-w absolute scale-0 top-[25vh] lg:top-[17vh] left-1/2 w-[65vw] translate-x-[-55%] rotate-[5deg] lg:w-[60vw] lg:rotate-6 pointer-events-none">
                    <motion.div
                        whileDrag={{ scale: 1.015, rotate: 5, transition: { duration: 0.5 }}}
                        drag
                        onDragStart={(e: MouseEvent | TouchEvent | PointerEvent) => changeZ(e)}
                        className={`${styles.hoverItem} leading-[0] aspect-[3/5] hover:cursor-grab active:cursor-grabbing overflow-hidden pointer-events-none lg:aspect-[5/3]`}
                    >
                        <Image
                            className="w-full h-full object-cover pointer-events-none"
                            src='/images/transition/seitaro_mono02.jpg'
                            alt="Image"
                            height={1336}
                            width={2000}
                            priority
                        />   
                    </motion.div>                    
                </div>
                <div className="js-pic-w absolute scale-0 top-[8vh] lg:top-[13vh] left-[40%] lg:left-1/2 w-[65vw] translate-x-[-44%] rotate-[-8deg] lg:w-[60vw] lg:rotate-0 lg:translate-x-[-30%] lg:translate-y-[16%] pointer-events-none">
                    <motion.div
                        whileDrag={{ scale: 1.015, rotate: 5, transition: { duration: 0.5 }}}
                        drag
                        onDragStart={(e: MouseEvent | TouchEvent | PointerEvent) => changeZ(e)}
                        className={`${styles.hoverItem} leading-[0] aspect-[3/5] hover:cursor-grab active:cursor-grabbing overflow-hidden pointer-events-none lg:aspect-[5/3]`}
                    >
                        <Image
                            className="w-full h-full object-cover pointer-events-none"
                            src='/images/transition/seitaro_mono03.jpg'
                            alt="Image"
                            height={1336}
                            width={2000}
                            priority
                        />   
                    </motion.div>                    
                </div>
                <div className="js-pic-w absolute scale-0 top-[12vh] left-1/2 w-[65vw] translate-x-[-44%] rotate-[6deg] lg:w-[60vw] lg:translate-x-[-42%] lg:rotate-[8deg] pointer-events-none">
                    <motion.div
                        whileDrag={{ scale: 1.015, rotate: 5, transition: { duration: 0.5 }}}
                        drag
                        onDragStart={(e: MouseEvent | TouchEvent | PointerEvent) => changeZ(e)}
                        className={`${styles.hoverItem} leading-[0] aspect-[3/5] hover:cursor-grab active:cursor-grabbing overflow-hidden pointer-events-none lg:aspect-[5/3]`}
                    >
                        <Image
                            className="w-full h-full object-cover pointer-events-none"
                            src='/images/transition/seitaro_mono04.jpg'
                            alt="Image"
                            height={1336}
                            width={2000}
                            priority
                        />   
                    </motion.div>                    
                </div>
            </div>

            {/* MOVIE */}
            <div className="absolute top-0 left-0 w-screen h-svh pointer-events-none overflow-x-hidden lg:overflow-x-auto z-[18]">
                <div ref={containerMovie} className="absolute top-1/2 left-1/2 w-[80vw] translate-x-[-50%] translate-y-[-50%] pointer-events-none lg:left-[20%] lg:w-[70vw] lg:translate-x-[-40%] opacity-0">
                    <motion.div
                        whileDrag={{ scale: 1.015, rotate: 5, transition: { duration: 0.5 }}}
                        onDragStart={(e) => {
                            if (e.target instanceof HTMLElement) {
                                const videoElement = e.target.querySelector('.js-pt-video');
                                if (videoElement instanceof HTMLElement) {
                                    videoElement.style.filter = 'grayscale(1) contrast(1.3)';
                                }
                            }
                        }}
                        
                        onDragEnd={(e) => {
                            if (e.target instanceof HTMLElement) {
                                const videoElement = e.target.querySelector('.js-pt-video');
                                if (videoElement instanceof HTMLElement) {
                                    videoElement.style.filter = 'grayscale(0) contrast(1)';
                                }
                            }
                        }}
                        drag
                        className="inside leading-[0] aspect-[3/5] w-full h-auto overflow-hidden hover:cursor-grab active:cursor-grabbing rotate-[4deg] lg:aspect-[5/3]"
                    >
                        <video ref={videoRef} src="/movies/seitaro_brand_movie01.mp4" loop muted playsInline className="js-pt-video w-full h-full object-cover pointer-events-none"></video>
                    </motion.div>
                </div>
            </div>

            {/* APNG LOGO COLOR */}
            <div ref={typeColorWrapRef} className={`absolute top-0 left-0 flex justify-center items-center w-screen h-svh overflow-hidden pointer-events-none z-[20]`} >
                <img ref={typeColorAPng} className={`${styles.typoColor} rotate-90 max-w-[none] w-[80vh] h-auto pointer-events-none opacity-0 lg:w-[88%] lg:rotate-0`} src="" alt="" src-path='/images/transition/typoColor.png' height='1440' width='991'/>
            </div>

            <LogoFooter
                singleLogoForPc={true}
                classLink=''
            />
        </>
    )
}